<template>
  <div class="setup-step">
    <h3>{{ s__('Setup|Step 2: Name and Username') }}</h3>
    <p class="step-description">
      {{
        s__(
          'Setup|Set your display name and username. They are required and will be visible to other users in projects.'
        )
      }}
    </p>

    <div class="form-row">
      <label>{{ s__('Profile|Username') }}</label>
      <input
        v-model="username"
        type="text"
        :disabled="saving"
        placeholder="username"
        class="input"
      />
      <div v-if="usernameError" class="error">{{ usernameError }}</div>
    </div>

    <div class="form-row">
      <label>{{ s__('Setup|Full name') }}</label>
      <input
        v-model="name"
        type="text"
        :disabled="saving"
        :placeholder="s__('Setup|Enter your full name')"
        class="input"
      />
      <div v-if="nameError" class="error">{{ nameError }}</div>
    </div>

    <div class="help-text">
      {{ s__('Setup|You can change this later in your profile settings.') }}
    </div>
    <div v-if="saveError" class="error">{{ saveError }}</div>
    <div v-if="saveSuccess" class="success">{{ s__('Setup|Profile saved successfully!') }}</div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { s__, sprintf } from '@/locale'

const props = defineProps({
  user: {
    type: Object,
    required: true,
  },
  saving: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['save'])

const name = ref('')
const username = ref('')
const originalName = ref('')
const originalUsername = ref('')
const nameError = ref('')
const usernameError = ref('')
const saveError = ref('')
const saveSuccess = ref(false)

const UPDATE_USER_NAME_MUTATION = gql`
  mutation UpdateUserName($userId: ID!, $name: String) {
    updateUserName(userId: $userId, name: $name) {
      user {
        id
        name
      }
      errors
    }
  }
`

const UPDATE_USER_USERNAME_MUTATION = gql`
  mutation UpdateUserUsername($userId: ID!, $username: String!) {
    updateUserUsername(userId: $userId, username: $username) {
      user {
        id
        username
      }
      errors
    }
  }
`

const { mutate: updateUserName } = useMutation(UPDATE_USER_NAME_MUTATION)
const { mutate: updateUserUsername } = useMutation(UPDATE_USER_USERNAME_MUTATION)

const USERNAME_REGEX = /^[a-zA-Z0-9_]+$/
const USERNAME_MIN_LENGTH = 3
const USERNAME_MAX_LENGTH = 64
const NAME_MIN_LENGTH = 2

// Initialize name from user
watch(
  () => props.user,
  userData => {
    if (userData) {
      name.value = userData.name || ''
      username.value = userData.username || ''
      originalName.value = userData.name || ''
      originalUsername.value = userData.username || ''
    }
  },
  { immediate: true }
)

function validateName(value) {
  const sanitizedName = (value || '').trim()
  if (!sanitizedName) {
    return s__('Setup|Full name is required')
  }
  if (sanitizedName.length < NAME_MIN_LENGTH) {
    return sprintf(s__('Profile|is too short (minimum is %{count} characters)'), {
      count: NAME_MIN_LENGTH,
    })
  }
  return ''
}

function validateUsername(value) {
  if (!value || !value.trim()) {
    return s__('Setup|Username is required')
  }
  if (!USERNAME_REGEX.test(value)) {
    return s__('Profile|only letters, numbers, and underscores are allowed')
  }
  if (value.length < USERNAME_MIN_LENGTH) {
    return sprintf(s__('Profile|is too short (minimum is %{count} characters)'), {
      count: USERNAME_MIN_LENGTH,
    })
  }
  if (value.length > USERNAME_MAX_LENGTH) {
    return sprintf(s__('Profile|is too long (maximum is %{count} characters)'), {
      count: USERNAME_MAX_LENGTH,
    })
  }
  return ''
}

function validateForm() {
  nameError.value = validateName(name.value)
  usernameError.value = validateUsername(username.value)
  return !nameError.value && !usernameError.value
}

// Check if there are unsaved changes
function hasChanges() {
  return name.value !== originalName.value || username.value !== originalUsername.value
}

function isComplete() {
  return !validateName(name.value) && !validateUsername(username.value)
}

// Reset changes
function reset() {
  name.value = originalName.value
  username.value = originalUsername.value
  nameError.value = ''
  usernameError.value = ''
  saveError.value = ''
  saveSuccess.value = false
}

// Expose methods to parent
defineExpose({
  hasChanges,
  isComplete,
  reset,
  async save() {
    if (!props.user?.id) return false

    saveError.value = ''
    saveSuccess.value = false
    nameError.value = ''
    usernameError.value = ''

    if (!validateForm()) {
      return false
    }

    try {
      const sanitizedName = name.value.trim()
      const sanitizedUsername = username.value.trim()

      const nameResult = await updateUserName({
        userId: props.user.id,
        name: sanitizedName,
      })

      if (nameResult?.data?.updateUserName?.errors?.length) {
        throw new Error(nameResult.data.updateUserName.errors.join(', '))
      }

      const usernameResult = await updateUserUsername({
        userId: props.user.id,
        username: sanitizedUsername,
      })

      if (usernameResult?.data?.updateUserUsername?.errors?.length) {
        throw new Error(usernameResult.data.updateUserUsername.errors.join(', '))
      }

      saveSuccess.value = true
      originalName.value = sanitizedName
      originalUsername.value = sanitizedUsername
      name.value = sanitizedName
      username.value = sanitizedUsername
      emit('save', { name: sanitizedName, username: sanitizedUsername })
      return true
    } catch (e) {
      saveError.value = e.message || 'Failed to update profile'
      return false
    }
  },
})
</script>

<style scoped>
.setup-step {
  max-width: 600px;
}

.setup-step h3 {
  margin-top: 0;
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.step-description {
  margin-bottom: 24px;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.form-row {
  margin-bottom: 16px;
}

.form-row label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
}

.input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.help-text {
  margin-top: 8px;
  font-size: 13px;
  color: #666;
}

.error {
  color: #d32f2f;
  font-size: 14px;
  margin-top: 8px;
}

.success {
  color: #2e7d32;
  font-size: 14px;
  margin-top: 8px;
}
</style>
